import { type ActionExecutionRequest, type ComponentAction, type NodeId, Type, type UIAction } from "~/models/dto";
import { abs } from "~/tools/abs";
import { getTime } from "~/tools/date-time";
import type { Time } from "~/tools/date-time/time";
import { filter } from "~/tools/filter";
import { groupBy } from "~/tools/group-by";
import { isPresent } from "~/tools/is-present";
import { map } from "~/tools/map";
import { pipe } from "~/tools/pipe";
import { prop } from "~/tools/prop";
import { sortBy } from "~/tools/sort-by";
import { toValues } from "~/tools/to-values";
import { Tuple } from "~/tools/tuple";

import type { TimeRange } from "../app/state/view/timeline/TimeRange";
import {
	type ComponentCheckState,
	type ComponentHealthHistory,
	type MonitorCheckStatus,
	type MonitorIdentifierSuggestion,
	getComponentCheckStatesFn,
	getComponentHealthHistoryFn,
	getFullComponentFn,
	lookupIdentifierFn,
} from "../common/openapi";
import { request } from "../generic/request";

import { queryMonitorCheckStatus } from "./monitors";
import {
	type UseFullQueryOptions,
	type UseMutationOptions,
	type UseMutationResult,
	type UseQueryOptions,
	type UseQueryResult,
	useIsMutating,
	useMutation,
	useQueries,
	useQuery,
} from "./react-query";

interface QueryFullComponentParams {
	componentIdOrIdentifier: number | NodeId | string;
	timestamp: undefined | Time;
}

export const queryFullComponent = (({ componentIdOrIdentifier, timestamp }) => {
	function queryFn() {
		return getFullComponentFn({
			componentIdOrIdentifier: String(componentIdOrIdentifier),
			topologyTime: getTime(timestamp),
		});
	}

	return {
		queryKey: Tuple("components", "full", componentIdOrIdentifier),
		queryFn,
		timestamp,
	} as const;
}) satisfies (params: QueryFullComponentParams) => UseFullQueryOptions;

export const useQueryComponentActionsById = <TResult = Array<ComponentAction>>(
	{
		componentId,
		timestamp,
	}: {
		componentId: number;
		timestamp?: null | Time;
	},
	options?: UseQueryOptions<Array<ComponentAction>, Error, TResult>,
): UseQueryResult<TResult, Error> =>
	useQuery(
		["components", componentId, "actions"],
		() =>
			request<Array<ComponentAction>>(`/api/components/${componentId}/actions`, {
				query: {
					queryTime: getTime(timestamp),
				},
			}),
		{ ...options, timestamp },
	);

export interface UseExecuteComponentActionParams {
	componentId: number;
	actionId: number;
	timestamp: undefined | Time;
	timeRange: TimeRange;
}

export const useExecuteComponentAction = <TContext>(
	{ componentId, actionId, timestamp, timeRange }: UseExecuteComponentActionParams,
	options?: Omit<UseMutationOptions<undefined | UIAction, Error, void, TContext>, "mutationFn" | "mutationKey">,
): UseMutationResult<undefined | UIAction, Error, void, TContext> =>
	useMutation(
		["components", componentId, "actions", "execute", actionId],
		() => {
			const body: ActionExecutionRequest = {
				_type: Type.ActionExecutionRequest,
				actionId,
				timeContext: {
					_type: Type.ActionExecutionTimeContext,
					topologyTime: timestamp?.valueOf(),
					telemetryTimeStart: timeRange.start().valueOf(),
					telemetryTimeEnd: timeRange.end?.valueOf(),
				},
			};

			return request<{ result?: UIAction }>(`/api/components/${componentId}/actions`, {
				method: "POST",
				body,
			}).then(({ result }) => result);
		},
		options,
	);

export interface UseIsComponentActionExecutingParams {
	componentId: number;
	actionId?: number;
}

export const useCountComponentActionExecuting = ({
	componentId,
	actionId,
}: UseIsComponentActionExecutingParams): number =>
	useIsMutating(["components", componentId, "actions", "execute", actionId]);

export interface UseComponentsIdentifierTemplatesParams {
	metricQueries: ReadonlyArray<string>;
	componentTypeName: string;
	/**
	 * The limit on how many suggestions should return in the response.
	 * Empirically discovered that any suggestion after 5th has a low score.
	 *
	 * @default 5
	 */
	topN?: number;
}

export const useComponentsIdentifierTemplates = <TResult = Array<MonitorIdentifierSuggestion>>(
	{ metricQueries, componentTypeName, topN = 5 }: UseComponentsIdentifierTemplatesParams,
	options?: UseQueryOptions<Array<MonitorIdentifierSuggestion>, Error, TResult>,
): {
	error: null | Error;
	isLoading: boolean;
	data: Array<TResult>;
} => {
	const result = useQueries(
		metricQueries.map((metricQuery) => ({
			...options,
			queryKey: ["components", "identifierTemplate", { metricQuery, componentTypeName, topN }],
			queryFn: async () => {
				const { suggestions } = await lookupIdentifierFn({
					body: { metricQuery, componentTypeName, topN },
				});

				return suggestions;
			},
		})),
	);

	return {
		error: result.find(({ error }) => error != null)?.error ?? null,
		isLoading: result.some(({ isLoading }) => isLoading),
		data: result.map(({ data }) => data).filter(isPresent),
	};
};

export function queryComponentHealthHistory({
	componentIdOrUrn,
	timeRange,
}: {
	componentIdOrUrn: string | number;
	timeRange: TimeRange;
}) {
	return {
		queryKey: ["components", "healthHistory", componentIdOrUrn],
		queryFn: () =>
			getComponentHealthHistoryFn({
				componentIdOrIdentifier: String(componentIdOrUrn),
				startTime: timeRange.start().getTime(),
				endTime: timeRange.endDate().getTime(),
			}),
		timeRange,
	} as const satisfies UseFullQueryOptions<ComponentHealthHistory, Error>;
}

export function queryComponentCheckStates({
	componentIdOrUrn,
	timeRange,
}: {
	componentIdOrUrn: string | number;
	timeRange: TimeRange;
}) {
	return {
		queryKey: ["components", "checkStates", componentIdOrUrn],
		queryFn: async () => {
			const { checkStates } = await getComponentCheckStatesFn({
				componentIdOrIdentifier: String(componentIdOrUrn),
				startTime: timeRange.start().getTime(),
				endTime: timeRange.endDate().getTime(),
			});

			return checkStates;
		},

		timeRange,
	} as const satisfies UseFullQueryOptions<Array<ComponentCheckState>, Error>;
}

export interface MonitorCheckStatusWithSiblings extends MonitorCheckStatus {
	siblings: ReadonlyArray<ComponentCheckState>;
}

export function useQueryComponentCheckStates(
	{
		componentIdOrUrn,
		timeRange,
		timestamp,
	}: {
		componentIdOrUrn: string | number;
		timeRange: TimeRange;
		timestamp: Date | undefined;
	},
	options?: Pick<UseQueryOptions, "refetchOnTimelineSummaryUpdate" | "keepPreviousData">,
): {
	isLoading: boolean;
	data: undefined | Array<MonitorCheckStatusWithSiblings>;
} {
	const { isLoading, data: checkStates } = useQuery({
		...queryComponentCheckStates({ componentIdOrUrn, timeRange }),
		...options,
	});

	const grouped = groupBy(checkStates ?? [], (state) => state.identifier);
	const queries = toValues(grouped).map((siblings) => {
		if (timestamp) {
			const withDistance = siblings.map((sibling) => {
				if (sibling.startTime <= timestamp.valueOf() && sibling.endTime >= timestamp.valueOf()) {
					return {
						sibling,
						dist: 0,
						time: timestamp.valueOf(),
					};
				}
				const distStart = abs(sibling.startTime - timestamp.valueOf());
				const distEnd = abs(sibling.endTime - timestamp.valueOf());
				return distStart < distEnd
					? {
							sibling,
							dist: distStart,
							time: sibling.startTime,
						}
					: {
							sibling,
							dist: distEnd,
							time: sibling.endTime - 1,
						};
			});
			const { sibling, time } = sortBy(withDistance, (elem) => elem.dist)[0];
			return {
				...queryMonitorCheckStatus({
					identifier: sibling.idOrIdentifier,
					timestamp: time,
				}),
				select: (status: MonitorCheckStatus) => ({
					...status,
					siblings,
				}),
				...options,
			};
		}
		const latest = sortBy(siblings, (sibling) => -sibling.endTime)[0];
		return {
			...queryMonitorCheckStatus({
				identifier: latest.identifier,
				timestamp: latest.endTime - 1,
			}),
			select: (status: MonitorCheckStatus) => ({
				...status,
				siblings,
			}),
			...options,
		};
	});

	const queryResult = useQueries(queries);
	return {
		isLoading: isLoading || queryResult.some(prop("isLoading")),
		data: checkStates == null ? undefined : pipe(queryResult, map(prop("data")), filter(isPresent)),
	};
}
